import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Check, CreditCard, User, Mail, Phone, Minus, Plus, ArrowLeft, Tag, X, Shield, Lock, BadgeCheck, Banknote, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for trust badges
const getTrustBadgeIcon = (icon) => {
  switch (icon) {
    case 'shield': return <Shield className="w-4 h-4 text-green-500" />;
    case 'lock': return <Lock className="w-4 h-4 text-green-500" />;
    case 'credit-card': return <CreditCard className="w-4 h-4 text-green-500" />;
    case 'check': return <BadgeCheck className="w-4 h-4 text-green-500" />;
    default: return <Shield className="w-4 h-4 text-green-500" />;
  }
};

const EventRegisterPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [checkoutSettings, setCheckoutSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" or "interac"
  const [interacSettings, setInteracSettings] = useState(null);
  const [interacBookingResult, setInteracBookingResult] = useState(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    ticket_type: "",
    quantity: 1
  });

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${eventId}`);
        setEvent(response.data);
        // Pre-select first available ticket type
        const availableTicket = response.data.ticket_options?.find(t => t.status !== 'sold_out');
        if (availableTicket) {
          setFormData(prev => ({ ...prev, ticket_type: availableTicket.name }));
        }
        
        // Fetch checkout settings for trust elements
        try {
          const siteSettingsRes = await axios.get(`${API}/site-settings`);
          if (siteSettingsRes.data?.checkout) {
            setCheckoutSettings(siteSettingsRes.data.checkout);
          }
        } catch (e) {
          // Ignore if not available
        }
        
        // Fetch Interac settings
        try {
          const interacRes = await axios.get(`${API}/admin/settings/interac`);
          setInteracSettings(interacRes.data);
        } catch (e) {
          // Ignore if not available
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        toast.error("Event not found");
        navigate("/events");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, navigate]);

  const selectedTicket = event?.ticket_options?.find(t => t.name === formData.ticket_type);
  const subtotal = selectedTicket ? selectedTicket.price * formData.quantity : 0;
  
  // Calculate discount
  const calculateDiscount = () => {
    if (!appliedCoupon || !subtotal) return 0;
    if (appliedCoupon.discount_type === "percentage") {
      return (subtotal * appliedCoupon.discount_value / 100);
    }
    return Math.min(appliedCoupon.discount_value, subtotal);
  };
  
  const discountAmount = calculateDiscount();
  const totalAmount = subtotal - discountAmount;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (delta) => {
    setFormData(prev => ({
      ...prev,
      quantity: Math.max(1, Math.min(10, prev.quantity + delta))
    }));
  };
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        type: "events",
        event_id: eventId
      });
      
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`Coupon applied: ${response.data.discount_value}${response.data.discount_type === 'percentage' ? '%' : ' CAD'} off!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid coupon code");
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.ticket_type !== "" && selectedTicket?.status !== 'sold_out';
      case 2:
        return formData.name !== "" && formData.email !== "" && formData.phone !== "";
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!isStepValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      if (paymentMethod === "interac") {
        // Create Interac event booking
        const response = await axios.post(`${API}/events/book-interac`, {
          event_id: eventId,
          ticket_type: formData.ticket_type,
          quantity: formData.quantity,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          origin_url: window.location.origin,
          coupon_code: appliedCoupon?.code || null
        });
        
        // Show Interac confirmation with payment details
        setInteracBookingResult(response.data);
        setStep(4); // Move to Interac confirmation step
      } else {
        // Create Stripe checkout session
        const response = await axios.post(`${API}/events/book`, {
          event_id: eventId,
          ticket_type: formData.ticket_type,
          quantity: formData.quantity,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          origin_url: window.location.origin,
          coupon_code: appliedCoupon?.code || null
        });

        // Redirect to Stripe checkout
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to process booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </main>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <main className="pt-20" data-testid="event-register-page">
      {/* Header */}
      <section className="section-padding-sm bg-[#050505] border-b border-[rgba(212,175,55,0.15)]">
        <div className="container-luxury">
          <button
            onClick={() => navigate("/events")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </button>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-2">
              Register for Event
            </p>
            <h1 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
              {event.title}
            </h1>
            <div className="flex flex-wrap gap-6 text-[#A1A1AA]">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D4AF37]" />
                <span>{event.time}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#D4AF37]" />
                <span>{event.location}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-6 bg-[#0F0F0F] sticky top-20 z-40">
        <div className="container-luxury">
          <div className="flex justify-center gap-8">
            {[
              { num: 1, label: "Select Tickets" },
              { num: 2, label: "Your Details" },
              { num: 3, label: "Payment" }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div
                  className={`w-10 h-10 flex items-center justify-center transition-all ${
                    step > s.num
                      ? "bg-[#D4AF37] text-[#050505]"
                      : step === s.num
                      ? "border-2 border-[#D4AF37] text-[#D4AF37]"
                      : "border border-[rgba(212,175,55,0.3)] text-[#52525B]"
                  }`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`hidden md:block ml-3 text-sm uppercase tracking-wider ${
                  step >= s.num ? "text-[#F5F5F0]" : "text-[#52525B]"
                }`}>
                  {s.label}
                </span>
                {i < 2 && (
                  <div className={`hidden md:block w-12 h-px ml-4 ${
                    step > s.num ? "bg-[#D4AF37]" : "bg-[rgba(212,175,55,0.2)]"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="section-padding bg-[#050505]">
        <div className="container-luxury max-w-4xl">
          {/* Step 1: Select Tickets */}
          {step === 1 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              data-testid="step-1"
            >
              <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                Select Your Tickets
              </h2>
              
              <div className="space-y-4 mb-8">
                {event.ticket_options?.map((ticket) => {
                  const isSoldOut = ticket.status === 'sold_out';
                  const isComingSoon = ticket.status === 'coming_soon';
                  const isDisabled = isSoldOut || isComingSoon;
                  
                  return (
                  <div
                    key={ticket.name}
                    onClick={() => !isDisabled && setFormData(prev => ({ ...prev, ticket_type: ticket.name }))}
                    className={`p-6 border transition-all ${
                      isDisabled 
                        ? "border-[rgba(212,175,55,0.1)] bg-[#0a0a0a] opacity-60 cursor-not-allowed"
                        : formData.ticket_type === ticket.name
                          ? "border-[#D4AF37] bg-[rgba(212,175,55,0.05)] cursor-pointer"
                          : "border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)] bg-[#121212] cursor-pointer"
                    }`}
                    data-testid={`ticket-option-${ticket.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className={`font-['Playfair_Display'] text-lg font-semibold ${isDisabled ? 'text-[#52525B]' : 'text-[#F5F5F0]'}`}>
                            {ticket.name}
                          </h3>
                          {ticket.name === 'VIP Glow-Up' && !isDisabled && (
                            <span className="bg-[#D4AF37] text-[#050505] px-2 py-0.5 text-xs uppercase tracking-widest">
                              Premium
                            </span>
                          )}
                          {isSoldOut && (
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 text-xs uppercase tracking-widest border border-red-500/30">
                              Sold Out
                            </span>
                          )}
                          {isComingSoon && (
                            <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 text-xs uppercase tracking-widest border border-blue-500/30">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        {ticket.description && (
                          <p className={`text-sm ${isDisabled ? 'text-[#3f3f46]' : 'text-[#A1A1AA]'}`}>{ticket.description}</p>
                        )}
                        {ticket.savings && !isDisabled && (
                          <p className="text-[#D4AF37] text-sm mt-1">{ticket.savings}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-2xl font-['Playfair_Display'] font-bold ${isDisabled ? 'text-[#52525B]' : 'gold-text'}`}>
                            C${ticket.price}
                          </p>
                          {ticket.per_person && (
                            <p className={`text-xs ${isDisabled ? 'text-[#3f3f46]' : 'text-[#A1A1AA]'}`}>(C${ticket.per_person} each)</p>
                          )}
                        </div>
                        {!isDisabled && (
                          <div className={`w-6 h-6 border-2 flex items-center justify-center ${
                            formData.ticket_type === ticket.name
                              ? "bg-[#D4AF37] border-[#D4AF37]"
                              : "border-[rgba(212,175,55,0.3)]"
                          }`}>
                            {formData.ticket_type === ticket.name && (
                              <Check className="w-4 h-4 text-[#050505]" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Quantity Selector */}
              <div className="luxury-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="form-label">Number of Tickets</label>
                    <p className="text-[#A1A1AA] text-sm">Maximum 10 tickets per order</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      disabled={formData.quantity <= 1}
                      className="w-10 h-10 border border-[rgba(212,175,55,0.3)] flex items-center justify-center hover:border-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                    <span className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0] w-12 text-center">
                      {formData.quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(1)}
                      disabled={formData.quantity >= 10}
                      className="w-10 h-10 border border-[rgba(212,175,55,0.3)] flex items-center justify-center hover:border-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Personal Details */}
          {step === 2 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              data-testid="step-2"
            >
              <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                Your Details
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="form-label">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="luxury-input pl-8"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="luxury-input pl-8"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="form-label">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="luxury-input pl-8"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>

              <p className="text-[#52525B] text-sm mt-6">
                * Confirmation details will be sent to your email address.
              </p>
            </motion.div>
          )}

          {/* Step 3: Review & Payment */}
          {step === 3 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              data-testid="step-3"
            >
              <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                Review & Payment
              </h2>
              
              <div className="grid md:grid-cols-2 gap-12">
                {/* Order Summary */}
                <div className="luxury-card p-8">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
                    Order Summary
                  </h3>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                      <span className="text-[#A1A1AA]">Event</span>
                      <span className="text-[#F5F5F0]">{event.title}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                      <span className="text-[#A1A1AA]">Date</span>
                      <span className="text-[#F5F5F0]">{formatDate(event.date)}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                      <span className="text-[#A1A1AA]">Ticket Type</span>
                      <span className="text-[#F5F5F0]">{formData.ticket_type}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                      <span className="text-[#A1A1AA]">Quantity</span>
                      <span className="text-[#F5F5F0]">{formData.quantity}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                      <span className="text-[#A1A1AA]">Price per ticket</span>
                      <span className="text-[#F5F5F0]">C${selectedTicket?.price}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                      <span className="text-[#A1A1AA]">Contact</span>
                      <span className="text-[#F5F5F0]">{formData.email}</span>
                    </div>
                  </div>
                  
                  {/* Coupon Code Section */}
                  <div className="mb-6 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                    <label className="text-[#D4AF37] text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Discount Code
                    </label>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-[#121212] p-3 border border-green-600/30">
                        <div>
                          <span className="text-green-500 font-semibold">{appliedCoupon.code}</span>
                          <span className="text-[#A1A1AA] text-sm ml-2">
                            ({appliedCoupon.discount_value}{appliedCoupon.discount_type === 'percentage' ? '%' : ' CAD'} off)
                          </span>
                        </div>
                        <button
                          onClick={handleRemoveCoupon}
                          className="text-[#A1A1AA] hover:text-red-500"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none uppercase"
                          data-testid="coupon-input"
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon || !couponCode.trim()}
                          className="btn-secondary px-4"
                          data-testid="apply-coupon-btn"
                        >
                          {validatingCoupon ? "..." : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Price Breakdown */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA]">Subtotal ({formData.quantity} × C${selectedTicket?.price})</span>
                      <span className="text-[#F5F5F0]">C${subtotal.toFixed(2)}</span>
                    </div>
                    {appliedCoupon && discountAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-500">Discount ({appliedCoupon.code})</span>
                        <span className="text-green-500">-C${discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 border-t border-[rgba(212,175,55,0.15)]">
                    <span className="text-[#F5F5F0] font-semibold">Total</span>
                    <span className="text-3xl font-['Playfair_Display'] font-bold gold-text">
                      C${totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Payment Info */}
                <div>
                  {/* Trust Badges */}
                  {checkoutSettings?.trust_badges && checkoutSettings.trust_badges.length > 0 && (
                    <div className="mb-6 p-4 border border-[rgba(212,175,55,0.2)] bg-[#0F0F0F]">
                      <div className="flex flex-wrap justify-center gap-4">
                        {checkoutSettings.trust_badges.map((badge, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                            {getTrustBadgeIcon(badge.icon)}
                            <span>{badge.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Method Selector */}
                  <div className="luxury-card p-6 mb-6">
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F0] mb-4">
                      Select Payment Method
                    </h3>
                    <div className="space-y-3">
                      {/* Credit Card Option */}
                      <button
                        onClick={() => setPaymentMethod("stripe")}
                        className={`w-full p-4 border transition-all flex items-center gap-4 ${
                          paymentMethod === "stripe"
                            ? "border-[#D4AF37] bg-[rgba(212,175,55,0.05)]"
                            : "border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)]"
                        }`}
                        data-testid="payment-method-stripe"
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === "stripe" ? "border-[#D4AF37]" : "border-[#52525B]"
                        }`}>
                          {paymentMethod === "stripe" && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                        </div>
                        <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                        <div className="text-left flex-1">
                          <p className="text-[#F5F5F0] font-medium">Credit / Debit Card</p>
                          <p className="text-[#52525B] text-xs">Visa, Mastercard, Amex • Powered by Stripe</p>
                        </div>
                      </button>
                      
                      {/* Interac Option */}
                      {interacSettings?.enabled && (
                        <button
                          onClick={() => setPaymentMethod("interac")}
                          className={`w-full p-4 border transition-all flex items-center gap-4 ${
                            paymentMethod === "interac"
                              ? "border-[#D4AF37] bg-[rgba(212,175,55,0.05)]"
                              : "border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)]"
                          }`}
                          data-testid="payment-method-interac"
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            paymentMethod === "interac" ? "border-[#D4AF37]" : "border-[#52525B]"
                          }`}>
                            {paymentMethod === "interac" && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                          </div>
                          <Banknote className="w-5 h-5 text-[#D4AF37]" />
                          <div className="text-left flex-1">
                            <p className="text-[#F5F5F0] font-medium">Interac e-Transfer</p>
                            <p className="text-[#52525B] text-xs">Pay directly from your bank account</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Stripe Payment Info */}
                  {paymentMethod === "stripe" && (
                    <div className="luxury-card p-8 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <CreditCard className="w-6 h-6 text-[#D4AF37]" />
                        <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
                          Secure Payment
                        </h3>
                      </div>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed">
                        You'll be redirected to our secure payment partner, Stripe, 
                        to complete your purchase. Your payment information is 
                        encrypted and secure.
                      </p>
                      
                      {/* Guarantee */}
                      {checkoutSettings?.guarantee_text && (
                        <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                          <p className="text-[#D4AF37] font-medium text-center">
                            {checkoutSettings.guarantee_text}
                          </p>
                        </div>
                      )}
                      
                      {/* Money Back Guarantee */}
                      {checkoutSettings?.show_money_back && (
                        <p className="text-center text-[#A1A1AA] text-sm mt-2">
                          {checkoutSettings.money_back_days || 7}-Day Money-Back Guarantee
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Interac Payment Info */}
                  {paymentMethod === "interac" && interacSettings?.enabled && (
                    <div className="luxury-card p-8 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Banknote className="w-6 h-6 text-[#D4AF37]" />
                        <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
                          Interac e-Transfer
                        </h3>
                      </div>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
                        {interacSettings.checkout_instructions || 
                          "After completing this form, you'll receive instructions to send your e-Transfer. Your tickets will be sent once we receive your payment."}
                      </p>
                      
                      <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.2)] space-y-3">
                        <div>
                          <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Send e-Transfer to:</p>
                          <p className="text-[#D4AF37] font-semibold">{interacSettings.email}</p>
                        </div>
                        {interacSettings.security_question && (
                          <div>
                            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Security Question:</p>
                            <p className="text-[#F5F5F0]">{interacSettings.security_question}</p>
                          </div>
                        )}
                        {interacSettings.security_answer && (
                          <div>
                            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Security Answer:</p>
                            <p className="text-[#D4AF37] font-semibold">{interacSettings.security_answer}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Terms Text */}
                  {checkoutSettings?.terms_text && (
                    <p className="text-[#52525B] text-xs mb-4 text-center">
                      {checkoutSettings.terms_text}
                    </p>
                  )}

                  <div className="text-[#52525B] text-sm space-y-2">
                    <p>• {checkoutSettings?.confirmation_email_note || "E-tickets sent to your email immediately after payment"}</p>
                    <p>• Please bring your confirmation email to the event</p>
                    <p>• Contact us for any changes or questions</p>
                  </div>
                  
                  {/* Support Text */}
                  {checkoutSettings?.support_text && (
                    <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                      <p className="text-center text-[#A1A1AA] text-sm">
                        {checkoutSettings.support_text}
                      </p>
                    </div>
                  )}
                  
                  {/* Payment Methods */}
                  {checkoutSettings?.payment_methods && checkoutSettings.payment_methods.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                      <p className="text-center text-[#52525B] text-xs mb-3">We Accept</p>
                      <div className="flex justify-center flex-wrap gap-3">
                        {checkoutSettings.payment_methods.includes('visa') && (
                          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/visa.svg" alt="Visa" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                        )}
                        {checkoutSettings.payment_methods.includes('mastercard') && (
                          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/mastercard.svg" alt="Mastercard" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                        )}
                        {checkoutSettings.payment_methods.includes('amex') && (
                          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/americanexpress.svg" alt="American Express" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                        )}
                        {checkoutSettings.payment_methods.includes('discover') && (
                          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/discover.svg" alt="Discover" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                        )}
                        {checkoutSettings.payment_methods.includes('paypal') && (
                          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/paypal.svg" alt="PayPal" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                        )}
                        {checkoutSettings.payment_methods.includes('interac') && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-[#1a1a1a] rounded border border-[rgba(212,175,55,0.2)]">
                            <span className="text-[#A1A1AA] text-xs font-medium">Interac</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Interac Confirmation */}
          {step === 4 && interacBookingResult && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              data-testid="step-4"
            >
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-20 h-20 bg-[#D4AF37] mx-auto mb-6 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-[#050505]" />
                </div>
                
                <h2 className="text-3xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                  Almost There!
                </h2>
                <p className="text-[#A1A1AA] text-lg mb-8">
                  Your tickets have been reserved. Complete your Interac e-Transfer to confirm.
                </p>
                
                {/* Booking Reference */}
                <div className="luxury-card p-6 mb-8">
                  <p className="text-[#52525B] text-sm uppercase tracking-wider mb-2">Your Booking Reference</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl font-mono text-[#D4AF37] font-bold">
                      {interacBookingResult.booking_reference}
                    </span>
                    <button
                      onClick={() => copyToClipboard(interacBookingResult.booking_reference)}
                      className="p-2 border border-[rgba(212,175,55,0.3)] hover:border-[#D4AF37] transition-colors"
                      data-testid="copy-reference-btn"
                    >
                      <Copy className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  </div>
                  <p className="text-[#52525B] text-xs mt-2">Include this reference in your e-Transfer message</p>
                </div>
                
                {/* Payment Instructions */}
                <div className="luxury-card p-8 text-left mb-8">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6 flex items-center gap-3">
                    <Banknote className="w-6 h-6 text-[#D4AF37]" />
                    Interac e-Transfer Instructions
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                      <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                        1
                      </div>
                      <div>
                        <p className="text-[#F5F5F0] font-medium mb-1">Send e-Transfer to:</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[#D4AF37] font-semibold">{interacBookingResult.interac_email}</span>
                          <button
                            onClick={() => copyToClipboard(interacBookingResult.interac_email)}
                            className="text-[#52525B] hover:text-[#D4AF37]"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                      <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                        2
                      </div>
                      <div>
                        <p className="text-[#F5F5F0] font-medium mb-1">Amount to send:</p>
                        <span className="text-2xl text-[#D4AF37] font-bold">C${interacBookingResult.total_amount.toFixed(2)}</span>
                        <p className="text-[#52525B] text-sm mt-1">
                          {interacBookingResult.quantity} ticket(s) × C${interacBookingResult.ticket_price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    
                    {interacBookingResult.security_question && (
                      <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                        <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                          3
                        </div>
                        <div>
                          <p className="text-[#F5F5F0] font-medium mb-1">Security Question:</p>
                          <p className="text-[#A1A1AA]">{interacBookingResult.security_question}</p>
                          <p className="text-[#52525B] text-sm mt-2">Answer: <span className="text-[#D4AF37] font-semibold">{interacBookingResult.security_answer}</span></p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                      <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                        {interacBookingResult.security_question ? "4" : "3"}
                      </div>
                      <div>
                        <p className="text-[#F5F5F0] font-medium mb-1">In the message, include:</p>
                        <p className="text-[#A1A1AA]">Your booking reference: <span className="text-[#D4AF37] font-mono font-bold">{interacBookingResult.booking_reference}</span></p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border border-[rgba(212,175,55,0.2)] bg-[#0F0F0F] mb-8">
                  <p className="text-[#A1A1AA] text-sm">
                    <strong className="text-[#D4AF37]">What happens next?</strong><br />
                    Once we receive your e-Transfer, we'll send your e-tickets to <strong>{interacBookingResult.email}</strong>. 
                    This usually takes 1-2 business hours during business days.
                  </p>
                </div>
                
                <Link
                  to={`/events/${eventId}`}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="back-to-event-btn"
                >
                  Back to Event
                </Link>
              </div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex justify-between mt-12 pt-8 border-t border-[rgba(212,175,55,0.15)]">
              {step > 1 ? (
                <button
                  onClick={() => setStep(step - 1)}
                  className="btn-secondary"
                  data-testid="prev-step-btn"
                >
                  Previous
                </button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <button
                  onClick={() => isStepValid() && setStep(step + 1)}
                  className={`btn-primary ${!isStepValid() ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={!isStepValid()}
                  data-testid="next-step-btn"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !isStepValid()}
                  className={`btn-primary flex items-center gap-2 ${
                    submitting || !isStepValid() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  data-testid="submit-payment-btn"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : paymentMethod === "interac" ? (
                    <>
                      <Banknote className="w-5 h-5" />
                      Reserve Tickets
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Pay C${totalAmount.toFixed(2)}
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default EventRegisterPage;
